import { HelpDeskUrl, productConfigByProductLine } from 'src/components/userManagement/ProductConfigByProductLine';
import { Product, WebApplication as WebApplicationGraphQL } from 'src/graphql/generated/types';
import { ModuleIdInCognito } from 'src/graphql/mappers/module';

type ProductDefinition = {
    downloadUrl: string;
    releaseDate: string;
    version: string;
    type: string;
    whatsNewUrl?: {
        de: string;
        en: string;
    };
}

export type WebApplicationViewModel = {
    kind: 'web';
    url: string;
    productLine: ProductLine;
    productName: string;
    logo: React.ReactNode;
    helpDeskUrl: HelpDeskUrl;
}

export type DesktopApplicationViewModel = {
    kind: 'desktop';
    productName: string;
    productLine: ProductLine;
    logo: React.ReactNode;
    helpDeskUrl: HelpDeskUrl;
    productDefinitions: ProductDefinition[];
}

export type ProductViewModel = Array<WebApplicationViewModel | DesktopApplicationViewModel>;

export type ProductId =
    | 'Optimize'
    | 'ColorProofGO'
    | 'ColorCard'
    | 'PSPlugin2'
    | 'ColorProofSetup'
    | 'ColorServer'
    | 'MeasureAppSetup'
    | 'OpenColorSetup'
    | 'ProofControl'
    | 'VectorPlugin'

export type DesktopAplication =
    | 'ColorServer'
    | 'ProofControl'
    | 'PhotoshopPlugin'
    | 'Measure'
    | 'ColorProof'
    | 'OpenColor'
    | 'VectorPlugin'

export type WebApplication =
    | 'Optimize'
    | 'ColorProofGO'
    | 'ColorCard'

export type ProductLine = DesktopAplication | WebApplication;

const moduleIdInCognitoToProductLine = new Map<ModuleIdInCognito, ProductLine>([
    ['ColorProofGO', 'ColorProofGO'],
    ['Optimize', 'Optimize'],
    ['Measure', 'Measure'],
    ['ColorCard', 'ColorCard'],
    ['PhotoshopPluginBasic', 'PhotoshopPlugin'],
    ['PhotoshopPluginPackaging', 'PhotoshopPlugin'],
    ['PhotoshopPluginINTERNAL', 'PhotoshopPlugin'],
    ['VectorPlugin', 'VectorPlugin'],
]);

const productIdToProductLine = new Map<ProductId, ProductLine>([
    ['ColorServer', 'ColorServer'],
    ['ProofControl', 'ProofControl'],
    ['PSPlugin2', 'PhotoshopPlugin'],
    ['MeasureAppSetup', 'Measure'],
    ['ColorProofSetup', 'ColorProof'],
    ['OpenColorSetup', 'OpenColor'],
    ['VectorPlugin', 'VectorPlugin'], // TODO: check why this is not in the query
    ['ColorCard', 'ColorCard'],
    ['ColorProofGO', 'ColorProofGO'],
    ['Optimize', 'Optimize'],
]);

export const mapCognitoIdToProductLine = (moduleIdInCognito: ModuleIdInCognito) => {
    return moduleIdInCognitoToProductLine.get(moduleIdInCognito)!;
};

const mapProductTypeToProductLine = (productType: string) => {
    return productIdToProductLine.get(productType as ProductId);
};

export const mapToWebApplicationViewModel = (app: WebApplicationGraphQL): WebApplicationViewModel => {
    const productLine = mapProductTypeToProductLine(app.productType)!;
    const { name, logo, helpDeskUrl } = productConfigByProductLine.get(productLine)!;
    return {
        kind: 'web',
        productLine,
        productName: name,
        url: app.url,
        logo,
        helpDeskUrl,
    };
};

export const mapToDesktopApplicationViewModels = (apps: Product[]): DesktopApplicationViewModel[] => {
    const filteredApps = apps.filter(app => mapProductTypeToProductLine(app.productType));

    return filteredApps.map(app => {
        const productLine = mapProductTypeToProductLine(app.productType)!;
        const { name, logo, helpDeskUrl } = productConfigByProductLine.get(productLine)!;
        return {
            kind: 'desktop',
            productName: name,
            logo,
            helpDeskUrl,
            productLine,
            productDefinitions: app.productDefinitions.map(({ downloadUrl, releaseDate, version, type, whatsNewUrl }) => ({
                downloadUrl,
                releaseDate,
                version,
                type,
                whatsNewUrl: whatsNewUrl ? { de: whatsNewUrl.de, en: whatsNewUrl.en } : undefined,
            })),
        };

    });
};